import { Warning } from "@mui/icons-material"
import Preview from "@mui/icons-material/Preview"
import { Chip, Stack, Tooltip, Typography } from "@mui/material"
import { DataGridProps } from "@mui/x-data-grid"
import { TFunction } from "i18next"
import moment from "moment"
import { Link } from "react-router-dom"
import { Locker } from "shared/locker"
import { getLockerDisplayValues } from "src/hooks/useLockerDisplayValues"

const defaultColWidth = 200

// The only fields the backend (SaGetLockers) can sort by. Every other column
// is marked sortable:false so the UI never asks for a sort the server ignores.
export const BACKEND_SORTABLE_FIELDS = ["name", "orgName", "lastSstSync"] as const

const monospace = (value?: string) => (
  <Typography variant="body2" fontFamily="monospace" noWrap>
    {value || "-"}
  </Typography>
)

type BuildColumnsOpts = {
  onSelect: (lockerId: string) => void
}

/**
 * Column set for the super-admin lockers grid (LOCKER-323). SST v1 + v2 ids get
 * their own columns; the tenant column reads the backend-denormalized `orgName`.
 */
export const buildSaLockersColumns = (
  t: TFunction,
  { onSelect }: BuildColumnsOpts,
): DataGridProps["columns"] => [
  {
    field: "name",
    headerName: t("name"),
    minWidth: defaultColWidth,
    flex: 1,
    renderCell: (params) => {
      const displayWarning = !params?.row?.sstLocker?.locker_grid
      const l = getLockerDisplayValues(params.row as Locker)
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Preview
            sx={{ cursor: "pointer", color: "gray" }}
            onClick={() => onSelect(params.row.id as string)}
          />
          <Link to={`/sa/lockers/${params.row.id}`}>
            <Typography variant="body2" noWrap>
              {params.value || l.i18n.name}
            </Typography>
          </Link>
          {displayWarning && (
            <Tooltip
              title={
                <Typography variant="body2" fontWeight="bold" noWrap>
                  Missing configuration
                </Typography>
              }
              placement="top-start"
              arrow
            >
              <Warning color="warning" fontSize="small" />
            </Tooltip>
          )}
          {params.row.isVirtual && (
            <Chip
              label="Virtual"
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
      )
    },
  },
  {
    field: "orgName",
    headerName: t("tenant"),
    minWidth: defaultColWidth,
    flex: 1,
    renderCell: (params) => (
      <Link to={`/sa/tenants/${params.row?.tenantId}`}>
        <Typography variant="body2" noWrap>
          {params.row?.orgName || "-"}
        </Typography>
      </Link>
    ),
  },
  {
    field: "sstApiVersion",
    headerName: t("sstVersion"),
    minWidth: 110,
    sortable: false,
    renderCell: (params) => {
      const v = params.row?.sstApiVersion === "v2" ? "v2" : "v1"
      return (
        <Chip
          label={v.toUpperCase()}
          size="small"
          color={v === "v2" ? "primary" : "default"}
          variant="outlined"
        />
      )
    },
  },
  {
    field: "sstV1Id",
    headerName: t("sstV1Id"),
    minWidth: defaultColWidth,
    sortable: false,
    valueGetter: (_value, row) => row?.sstLocker?._id || "",
    renderCell: (params) => monospace(params.row?.sstLocker?._id),
  },
  {
    field: "sstV2Id",
    headerName: t("sstV2Id"),
    minWidth: defaultColWidth,
    sortable: false,
    valueGetter: (_value, row) => row?.sst2Locker?.id || "",
    renderCell: (params) => monospace(params.row?.sst2Locker?.id),
  },
  {
    field: "nbDoors",
    headerName: t("nbDoors"),
    minWidth: defaultColWidth,
    sortable: false,
    renderCell: (params) => {
      const doors =
        params.row?.sstLocker?.doors || params.row?.sst2Locker?.doors || []
      const nbIsEnabled =
        doors.filter((d: any) => d.is_enable)?.length || 0
      const nbHasReservation =
        doors.filter((d: any) => d?.links?.length > 0)?.length || 0
      return (
        <Typography variant="body2" noWrap>
          {`${doors.length} (${nbIsEnabled} enabled, ${nbHasReservation} reserved)`}
        </Typography>
      )
    },
  },
  {
    field: "lastSstSync",
    headerName: t("lastSync"),
    minWidth: defaultColWidth,
    renderCell: (params) => (
      <Typography variant="body2" noWrap>
        {params.row?.lastSstSync
          ? moment(params.row?.lastSstSync).fromNow()
          : "never"}
      </Typography>
    ),
  },
  {
    field: "description",
    headerName: t("description"),
    minWidth: defaultColWidth,
    sortable: false,
  },
  {
    field: "sstLockerId",
    headerName: t("sstLockerId"),
    minWidth: defaultColWidth,
    sortable: false,
    renderCell: (params) => monospace(params.row?.sstLockerId),
  },
  {
    field: "city",
    headerName: t("city"),
    minWidth: 150,
    sortable: false,
  },
  {
    field: "province",
    headerName: t("province"),
    minWidth: 120,
    sortable: false,
  },
  {
    field: "country",
    headerName: t("country"),
    minWidth: 120,
    sortable: false,
  },
  {
    field: "displayOrder",
    headerName: t("displayOrder"),
    minWidth: 130,
    sortable: false,
  },
  {
    field: "createdAt",
    headerName: `📅 ${t("createdAt")}`,
    minWidth: defaultColWidth,
    sortable: false,
  },
  {
    field: "updatedAt",
    headerName: `📅 ${t("updatedAt")}`,
    minWidth: defaultColWidth,
    sortable: false,
  },
]
